/**
 * @fileoverview Import transactions page with CSV upload workflow.
 * @module features/universal-transactions/pages/ImportTransactionsPage
 */

import type { ReactElement } from 'react'
import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import CsvUploader from '../components/upload/CsvUploader'
import ImportPreviewSection from '../components/upload/ImportPreviewSection'
import { universalTransactionsApi } from '..'
import type {
  PreviewResponse,
  IngestionResponse,
} from '../schemas/universal-transactions'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { Card } from '@/shared/components/ui'

/**
 * ImportTransactionsPage
 *
 * CSV import workflow: Upload → Preview → Mapping → Import → Results
 *
 * @component
 */
export default function ImportTransactionsPage(): ReactElement {
  const { activeOrgId } = useActiveOrg()
  const navigate = useNavigate()
  const { t } = useTranslation('features-universal-transactions')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [step, setStep] = useState<'upload' | 'preview' | 'import' | 'results'>(
    'upload'
  )
  const [mappings, setMappings] = useState<Array<{ id: string; name: string; createdAt: string }>>(
    []
  )
  const [selectedMappingId, setSelectedMappingId] = useState<string | null>(null)
  const [mappingsLoaded, setMappingsLoaded] = useState(false)
  const [isIngesting, setIsIngesting] = useState(false)
  const [ingestError, setIngestError] = useState<string | null>(null)
  const [ingestResult, setIngestResult] = useState<IngestionResponse | null>(null)

  const slug = (s: string): string =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  const rand8 = (): string => Math.random().toString(36).slice(2, 10)
  const yyyymmdd = (d: Date): string => toDateOnlyStringLocal(d).replaceAll('-', '')

  const buildSourceFileKey = (fileName: string): string => {
    const org = (activeOrgId as unknown as string) || 'no-org'
    const now = new Date()
    return `universal-transactions/${org}/${yyyymmdd(now)}/${Date.now()}-${rand8()}-${slug(fileName)}`
  }

  const extractServerMessage = (data: unknown): string | null => {
    try {
      if (!data) return null
      if (typeof data === 'string') {
        return data.slice(0, 600)
      }
      if (typeof data !== 'object') return null
      const anyData = data as {
        message?: unknown
        fieldErrors?: Array<{ field?: string; message?: string }>
      }
      if (Array.isArray(anyData.fieldErrors) && anyData.fieldErrors.length > 0) {
        const parts = anyData.fieldErrors
          .map(fe => [fe.field, fe.message].filter(Boolean).join(': '))
          .filter(Boolean)
        return parts.join('\n') || null
      }
      if (typeof anyData.message === 'string' && anyData.message.trim().length > 0) {
        return anyData.message
      }
      return null
    } catch {
      return null
    }
  }

  const formatPreviewError = (err: unknown): string => {
    const axiosLike = err as { response?: { status?: number; data?: unknown } }
    const status = axiosLike?.response?.status
    const serverMsg = extractServerMessage(axiosLike?.response?.data)

    if (status === 413) {
      return t('errors.preview.tooLarge', {
        defaultValue: 'The file is too large. Please upload a file ≤ 10MB.',
      })
    }
    if (status === 415) {
      return (
        t('errors.preview.unsupportedType', {
          defaultValue: 'Unsupported file type. Only CSV files are accepted (text/csv).',
        }) + (serverMsg ? `\nDetails: ${serverMsg}` : '')
      )
    }
    if (status === 400) {
      // Likely CSV parsing/validation issue
      const hint = t('preview.hint.csvFormat', {
        defaultValue:
          'Ensure the CSV has a header row, uses comma or semicolon delimiters, and is UTF-8 encoded.',
      })
      return (
        t('errors.preview.invalidFormat', { defaultValue: 'Invalid CSV format. {{hint}}', hint }) +
        (serverMsg ? `\nDetails: ${serverMsg}` : '')
      )
    }
    if (serverMsg) return `Preview failed (${status ?? 'unknown'}). Details: ${serverMsg}`
    return `Failed to generate preview${typeof status === 'number' ? ` (HTTP ${status})` : ''}. Please verify the CSV file and try again.`
  }

  const handleFileSelect = async (file: File): Promise<void> => {
    setError(null)
    setIsLoading(true)
    try {
      const key = buildSourceFileKey(file.name)
      setUploadedFile(file)
      // Debug: log request context
      console.debug('[UniversalTransactions] previewCsv request', {
        sourceFileKey: key,
        orgId: activeOrgId,
        file: { name: file.name, size: file.size, type: file.type },
      })
      const res = await universalTransactionsApi.previewCsv(file, key)
      setPreview(res)
      // Auto-select suggested mapping if available
      if (res.suggestedMappingId) {
        setSelectedMappingId(res.suggestedMappingId)
      }
      setStep('preview')
    } catch (e: unknown) {
      // Log full error for debugging in dev tools
      console.error('[UniversalTransactions] previewCsv failed', e)
      const msg = formatPreviewError(e)
      setError(msg)
      toast.error(msg)
    } finally {
      setIsLoading(false)
    }
  }

  const resetToUpload = (): void => {
    setPreview(null)
    setUploadedFile(null)
    setSelectedMappingId(null)
    setError(null)
    setStep('upload')
  }

  // Load mappings when we enter preview step
  const loadMappings = useCallback(async (): Promise<void> => {
    try {
      const list = await universalTransactionsApi.listMappings()
      setMappings(list)
    } catch (e) {
      console.error('[UniversalTransactions] list mappings failed', e)
      const msg = t('mapping.errors.loadMappings', {
        defaultValue: 'Failed to load mappings. Please retry.',
      })
      toast.error(msg)
    } finally {
      setMappingsLoaded(true)
    }
  }, [t])

  useEffect(() => {
    if (step === 'preview' && !mappingsLoaded) {
      void loadMappings()
    }
  }, [step, mappingsLoaded, loadMappings])

  const handleIngest = async (): Promise<void> => {
    if (!uploadedFile) {
      toast.error(
        t('errors.noFileToImport', {
          defaultValue: 'No file to import. Please upload a CSV again.',
        })
      )
      return
    }
    if (!selectedMappingId) {
      toast.error(
        t('errors.selectMappingFirst', {
          defaultValue: 'Please select a mapping before importing.',
        })
      )
      return
    }
    setIsIngesting(true)
    setIngestError(null)
    try {
      const res = await universalTransactionsApi.ingestCsv(uploadedFile, selectedMappingId)
      setIngestResult(res)
      setStep('results')
      toast.success(t('toasts.importCompleted', { defaultValue: 'Import completed' }))
    } catch (e: unknown) {
      console.error('[UniversalTransactions] ingest failed', e)
      const msg = formatPreviewError(e)
      setIngestError(msg)
      toast.error(msg)
    } finally {
      setIsIngesting(false)
    }
  }

  return (
    <div className="min-h-screen pt-9 bg-card">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-8 space-y-6">
        <div className="p-6 pb-3 flex items-center justify-between bg-card rounded-lg">
          <h1 className="text-2xl font-bold text-foreground">
            {t('page.titleImport', { defaultValue: 'Import Transactions' })}
          </h1>
        </div>

        <Card className="glass-card p-6 space-y-6">
            {!preview && (
              <div className="space-y-3">
                <h2 className="text-base font-medium">
                  {t('upload.title', { defaultValue: 'Upload CSV' })}
                </h2>
                <CsvUploader onFileSelect={handleFileSelect} isLoading={isLoading} />
                {error && (
                  <div role="alert" className="text-sm text-destructive">
                    <pre className="whitespace-pre-wrap font-sans">{error}</pre>
                  </div>
                )}
                <div className="flex justify-start">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(-1)}
                  >
                    {t('common.cancel', { defaultValue: 'Cancel' })}
                  </Button>
                </div>
              </div>
            )}

            {/* Preview section */}
            {preview && step === 'preview' && (
              <ImportPreviewSection
                preview={preview}
                error={error}
                mappings={mappings}
                selectedMappingId={selectedMappingId}
                isIngesting={isIngesting}
                onMappingSelect={setSelectedMappingId}
                onImport={handleIngest}
                onReset={resetToUpload}
                onMappingCreated={async () => {
                  setMappingsLoaded(false)
                  await loadMappings()
                }}
              />
            )}


            {step === 'results' && ingestResult && (
              <div className="space-y-4">
                <h2 className="text-base font-medium">
                  {t('results.title', { defaultValue: 'Import results' })}
                </h2>
                <div className="rounded-md border p-4 text-sm">
                  <div>
                    {t('results.batchRef', { defaultValue: 'Batch ref' })}:{' '}
                    <strong>{ingestResult.batchRef}</strong>
                  </div>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      {t('results.totalRows', { defaultValue: 'Total rows' })}:{' '}
                      <strong>{ingestResult.totalRows}</strong>
                    </div>
                    <div>
                      {t('results.inserted', { defaultValue: 'Inserted' })}:{' '}
                      <strong className="text-green-600">{ingestResult.inserted}</strong>
                    </div>
                    <div>
                      {t('results.updated', { defaultValue: 'Updated' })}:{' '}
                      <strong>{ingestResult.updated}</strong>
                    </div>
                    <div>
                      {t('results.skipped', { defaultValue: 'Skipped' })}:{' '}
                      <strong className="text-amber-600">{ingestResult.skipped}</strong>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-md border border-border/60 p-3 bg-muted/20">
                      <div className="text-xs text-muted-foreground">
                        {t('results.duplicates', { defaultValue: 'Duplicates' })}
                      </div>
                      <div className="text-base font-medium">{ingestResult.duplicates}</div>
                    </div>
                    <div className="rounded-md border border-border/60 p-3 bg-muted/20">
                      <div className="text-xs text-muted-foreground">
                        {t('results.parseErrors', { defaultValue: 'Parse errors' })}
                      </div>
                      <div className="text-base font-medium">{ingestResult.parseErrors}</div>
                    </div>
                    <div className="rounded-md border border-border/60 p-3 bg-muted/20">
                      <div className="text-xs text-muted-foreground">
                        {t('results.validationErrors', { defaultValue: 'Validation errors' })}
                      </div>
                      <div className="text-base font-medium">{ingestResult.validationErrors}</div>
                    </div>
                  </div>
                  {Array.isArray(ingestResult.errorSamples) && ingestResult.errorSamples.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs font-medium mb-1">
                        {t('results.errorSamples', { defaultValue: 'Error samples' })}
                      </div>
                      <ul className="list-disc pl-5 space-y-1">
                        {ingestResult.errorSamples.slice(0, 5).map((msg: string, idx: number) => (
                          <li key={idx} className="text-xs text-muted-foreground">
                            {msg}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {ingestResult.parseErrors > 0 && (
                    <div className="mt-3 rounded-md border border-amber-400/40 bg-amber-50/50 p-3 text-xs text-amber-900">
                      {t('results.parseHint', {
                        defaultValue:
                          'Some rows failed to parse. Tip: adjust Amount parsing hints in your Mapping (Decimal/Thousands) and retry.',
                      })}
                    </div>
                  )}
                </div>
                {ingestError && (
                  <div role="alert" className="text-sm text-destructive">
                    <pre className="whitespace-pre-wrap font-sans">{ingestError}</pre>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={resetToUpload}>
                    Start new import
                  </Button>
                  <Button
                    type="button"
                    onClick={() =>
                      navigate(`/dashboard/transactions/universal?batchRef=${ingestResult.batchRef}` as string, {
                        state: {
                          ingestCounts: {
                            inserted: ingestResult.inserted,
                            updated: ingestResult.updated,
                            skipped: ingestResult.skipped,
                          },
                        },
                      })
                    }
                  >
                    View transactions
                  </Button>
                </div>
              </div>
            )}
        </Card>
      </div>
    </div>
  )
}
